import merge from 'deepmerge';
import { customBlue, customGreen, customGray, inputBorderColor, typePrimaryColor } from './colors';
import { green, grey, indigo, red } from '@mui/material/colors';
import { THEMES } from 'constants/Theme';

const defaultVariant = {
  name: THEMES.DEFAULT,
  palette: {
    mode: 'light',
    blue: {
      primary: { main: '#2ea8fd', light: '#A3D3F5', ultraLight: '#EAF6FF' }
    },
    red: {
      primary: { main: '#FC4540', light: '#FEB5B3', ultraLight: '#FFECEC' }
    },
    yellow: {
      primary: { main: '#FABC1C', light: '#FDE4A4', ultraLight: '#FFF8E8' }
    },
    primary: {
      main: customGreen[100],
      light: customGreen[40],
      ultraLight: customGreen[10],
      contrastText: '#FFF',
      custom: customGreen[80]
    },
    secondary: {
      main: customGray[90],
      secondary: customGray[70],
      light: customGray[40],
      contrastText: '#FFF',
      input: inputBorderColor
    },
    background: {
      default: '#F7F9FC',
      paper: '#FFF'
    },
    text: {
      primary: typePrimaryColor
    },
    button: {
      primary: { default: customGray[70], hover: typePrimaryColor, disabled: '#f0f0f0' },
      secondary: {
        default: customGray[0],
        hover: '#f0f0f0',
        disabled: customGray[0]
      }
    }
  },
  header: {
    color: grey[500],
    background: '#FFF',
    search: {
      color: grey[800]
    },
    indicator: {
      background: customBlue[600]
    }
  },

  footer: {
    color: grey[500],
    background: '#FFF'
  },
  menu: {
    color: grey[500],
    background: grey[100]
  },
  sidebar: {
    color: grey[200],
    background: customGray[90],
    project: customGray[110],
    header: {
      color: grey[200],
      background: customGray[90],
      brand: {
        color: customBlue[500]
      }
    },
    input: {
      background: customGray[95]
    },
    footer: {
      color: grey[200],
      background: customGray[90],
      online: {
        background: green[500]
      }
    },
    badge: {
      color: '#FFF',
      background: customBlue[500]
    }
  }
} as const;

const darkVariant = merge(defaultVariant, {
  name: THEMES.DARK,
  palette: {
    mode: 'dark',
    primary: {
      main: customBlue[600],
      contrastText: '#FFF'
    },
    background: {
      default: '#1B2635',
      paper: '#233044'
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.5)'
    }
  },
  header: {
    color: grey[300],
    background: '#1B2635',
    search: {
      color: grey[200]
    }
  },
  footer: {
    color: grey[300],
    background: '#233044'
  }
});

const lightVariant = merge(defaultVariant, {
  name: THEMES.LIGHT,
  palette: {
    mode: 'light'
  },
  header: {
    color: grey[200],
    background: customBlue[800],
    search: {
      color: grey[100]
    },
    indicator: {
      background: red[700]
    }
  },
  sidebar: {
    color: grey[900],
    background: '#FFF',
    header: {
      color: '#FFF',
      background: customBlue[800],
      brand: {
        color: '#FFFFFF'
      }
    },
    footer: {
      color: grey[800],
      background: '#F7F7F7',
      online: {
        background: green[500]
      }
    }
  }
});

const blueVariant = merge(defaultVariant, {
  name: THEMES.BLUE,
  palette: {
    mode: 'light'
  },
  sidebar: {
    color: '#FFF',
    background: customBlue[700],
    header: {
      color: '#FFF',
      background: customBlue[800],
      brand: {
        color: '#FFFFFF'
      }
    },
    footer: {
      color: '#FFF',
      background: customBlue[800],
      online: {
        background: '#FFF'
      }
    },
    badge: {
      color: '#000',
      background: '#FFF'
    }
  }
});

const greenVariant = merge(defaultVariant, {
  name: THEMES.GREEN,
  palette: {
    primary: {
      main: green[800],
      contrastText: '#FFF'
    },
    secondary: {
      main: green[500],
      contrastText: '#FFF'
    }
  },
  header: {
    indicator: {
      background: green[600]
    }
  },
  sidebar: {
    color: '#FFF',
    background: green[700],
    header: {
      color: '#FFF',
      background: green[800],
      brand: {
        color: '#FFFFFF'
      }
    },
    footer: {
      color: '#FFF',
      background: green[800],
      online: {
        background: '#FFF'
      }
    },
    badge: {
      color: '#000',
      background: '#FFF'
    }
  }
});

const indigoVariant = merge(defaultVariant, {
  name: THEMES.INDIGO,
  palette: {
    primary: {
      main: indigo[600],
      contrastText: '#FFF'
    },
    secondary: {
      main: indigo[400],
      contrastText: '#FFF'
    }
  },
  header: {
    indicator: {
      background: indigo[600]
    }
  },
  sidebar: {
    color: '#FFF',
    background: indigo[700],
    header: {
      color: '#FFF',
      background: indigo[800],
      brand: {
        color: '#FFFFFF'
      }
    },
    footer: {
      color: '#FFF',
      background: indigo[800],
      online: {
        background: '#FFF'
      }
    },
    badge: {
      color: '#000',
      background: '#FFF'
    }
  }
});

const variants = [
  defaultVariant,
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant
];

export default variants;
